/* eslint-disable max-lines-per-function */
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import Cookies from 'js-cookie';
import { getCookieDomain, getEnvironment } from '@/utils';
import { useStandorte } from '@/features/standortContext/hooks/useStandorte';
import { Standort } from '@/features/standortContext/entities/standort';
import { debounce } from 'lodash';
import * as Sentry from '@sentry/react';
import { useProfile } from '@/hooks';
import i18next from 'i18next';
import { translationMapper } from '@/features/standortContext/entities/translationMapper';

type StandortContextProps = {
  standorte: Standort[];
  currentStandort: Standort;
  updateStandort: (standort: Standort) => void;
  updateToNext: () => void;
  updateToPrevious: () => void;
};

const StandortContext = createContext<StandortContextProps | undefined>(
  undefined,
);

export function StandortContextProvider({ children }: { children: ReactNode }) {
  const { standorte, hauptStandort } = useStandorte();
  const { manager } = useProfile();

  let standortCookie = Cookies.get('staging_standort');
  if (getEnvironment() === 'production') {
    standortCookie = Cookies.get('standort');
  }

  const initialStandort =
    standorte.find(({ id }) => id === standortCookie) ?? hauptStandort;

  const [currentStandort, setCurrentStandort] = useState<Standort>(
    initialStandort!,
  );

  // MV2-1805 Standortinfos loggen, um herauszufinden, warum manchmal der Hauptstandort nicht gefunden wird
  if (!currentStandort) {
    Sentry.setTag(
      'standorte',
      JSON.stringify(
        standorte.map((s) => {
          return {
            id: s.id,
            name: s.name,
          };
        }),
      ),
    );
    Sentry.setTag('hauptstandort id', manager?.standortId);
  }

  const handleUpdate = useCallback((standort: Standort) => {
    if (getEnvironment() === 'production') {
      Cookies.set('standort', standort.id, {
        domain: getCookieDomain(),
        expires: 365,
      });
    }
    Cookies.set('staging_standort', standort.id, {
      domain: getCookieDomain(),
      expires: 365,
    });
    setCurrentStandort(standort);
  }, []);

  const updateStandortRef = useRef(debounce(handleUpdate, 200));

  useEffect(() => {
    // If handleUpdate ever changes, replace the debounced function
    updateStandortRef.current = debounce(handleUpdate, 200);
    return () => {
      updateStandortRef.current.cancel();
    };
  }, [handleUpdate]);

  // Wenn sich der Standort ändert, dann prüfe ob die Übersetzungen ggf geupdated werden müssen
  useEffect(() => {
    if (currentStandort) {
      i18next.changeLanguage(translationMapper[currentStandort.land]);
    }
  }, [currentStandort]);

  const updateStandort = (standort: Standort) => {
    updateStandortRef.current(standort);
  };

  function updateToNext() {
    const idxOfCurrentStandort = standorte.findIndex(
      (standort) => standort.id === currentStandort.id,
    );
    const nextStandort =
      idxOfCurrentStandort === standorte.length - 1
        ? standorte[0]
        : standorte[idxOfCurrentStandort + 1];
    updateStandort(nextStandort);
  }

  function updateToPrevious() {
    const idxOfCurrentStandort = standorte.findIndex(
      (standort) => standort.id === currentStandort.id,
    );
    const previousStandort =
      idxOfCurrentStandort === 0
        ? standorte[standorte.length - 1]
        : standorte[idxOfCurrentStandort - 1];
    updateStandort(previousStandort);
  }

  return (
    <StandortContext.Provider
      value={{
        standorte,
        currentStandort,
        updateStandort,
        updateToNext,
        updateToPrevious,
      }}>
      {children}
    </StandortContext.Provider>
  );
}

// Würde ich hier disablen, damit Context und der zugehörige Hook zusammenstehen
// eslint-disable-next-line react-refresh/only-export-components
export function useStandortContext() {
  const context = useContext(StandortContext);

  if (!context) {
    throw new Error(
      'useStandortContext must be used within StandortContextProvider',
    );
  }
  return context;
}
